import { gql, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useUserManagementStore } from '@modules/users/store/hooks';
import { UserManagementActionType } from '@modules/users/store/userManagementActionType';
import { GetTenantByIdQueryVariables, Query } from 'generated/graphql';
import { useEffect } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const FETCH_TENANT_AUTO_APPROVAL_STATE = gql`
  query getTenantAutoApprovalState($tenantID: ID!) {
    getTenantByID(id: $tenantID) {
      dashboardSettings {
        isAutoApprovalDisabled
      }
    }
  }
`;

interface UseFetchAutoApprovalStateOptions {
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

export const useFetchTenantAutoApprovalState = (
  options?: UseFetchAutoApprovalStateOptions,
) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const dispatch = useUserManagementStore(({ dispatch }) => dispatch);

  const { data, refetch, loading } = useQuery<
    Query,
    GetTenantByIdQueryVariables
  >(FETCH_TENANT_AUTO_APPROVAL_STATE, {
    variables: {
      tenantID,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: options?.fetchPolicy ?? 'cache-and-network',
    skip: options?.skip,
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to fetch auto approval state',
        props: {},
      });
    },
    onCompleted: (data) => {
      dispatch({
        payload: {
          isAutoApprovalDisabled:
            data.getTenantByID?.dashboardSettings?.isAutoApprovalDisabled ??
            false,
        },
        type: UserManagementActionType.SET_AUTO_APPROVAL_STATE,
      });
    },
  });

  const isLoading = !data && loading;

  useEffect(() => {
    if (data?.getTenantByID) {
      dispatch({
        payload: {
          isAutoApprovalDisabled:
            data.getTenantByID.dashboardSettings?.isAutoApprovalDisabled ??
            false,
        },
        type: UserManagementActionType.SET_AUTO_APPROVAL_STATE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.getTenantByID]);

  useEffect(() => {
    dispatch({
      type: UserManagementActionType.SET_VISUAL,
      payload: { isFetchingAutoApprovalState: isLoading },
    });
  }, [isLoading, dispatch]);

  return {
    isAutoApprovalDisabled:
      data?.getTenantByID?.dashboardSettings?.isAutoApprovalDisabled,
    isLoading,
    refetch,
  };
};
